import { useEffect, useState } from 'preact/hooks'
import { useForm, Controller } from 'react-hook-form'
import { Settings as SettingsIcon, FileCheck, Users, Shield, ScanSearch, Loader2 } from 'lucide-preact'
import { UserPicker, Select, Checkbox, FormActions } from '@/components'
import { BRAND_CLASSES } from '@/constants/theme'
import { useToast } from '@/hooks/useToast'
import { useAssetQaVisible } from '@/hooks/useAssetQaVisible'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { useLookups } from '@/hooks/useLookups'
import { InheritSettingsToggle } from '@/components/common/InheritSettingsToggle'
import {
  workflowSettingsApi,
  type AssetWorkflowSettingsViewDto,
  type AssetWorkflowSettingsDto,
  type WorkflowSettingsUserDto,
  type WorkflowSettingsLevel
} from '@/api/workflowSettings'
import { loadEffectiveSettings } from '@/state/workflowSettings'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface Props {
  level: WorkflowSettingsLevel
  clientCompanyId: string
  workspaceId: string
  campaignId?: string
  webId?: string
}

interface WorkflowFormData {
  inheritFromParent: boolean
  defaultReviewerUsers: WorkflowSettingsUserDto[]
  internalTeamUsers: WorkflowSettingsUserDto[]
  defaultStatusOnUpload: string
  requireSignOffConfirmation: boolean
  allowCommentsAfterSignOff: boolean
  internalOnlyReopen: boolean
  holdVerdictWhenBrandChecksCannotRun: boolean
  allowImages: boolean
  allowDocuments: boolean
  allowVideos: boolean
  allowArchives: boolean
  allowedExtensions: string
}

// One shared instance rather than a fresh `[]` per render. UserPicker keeps `selectedUsers` in its search
// effect's dependencies, so a new array identity each render re-runs the effect and its cleanup aborts the
// request already in flight — the search then never lands its results (ORB-719).
//
// Frozen because the instance is shared: a stray `push` here would leak into every picker that fell back to
// it. Modules are strict mode, so a mutation throws rather than being dropped on the floor. The declared
// type stays mutable to match the `selectedUsers` prop; the guarantee is the runtime one.
const NO_SELECTED_USERS: WorkflowSettingsUserDto[] = []
Object.freeze(NO_SELECTED_USERS)

export function AssetWorkflowSettings({ level, workspaceId, clientCompanyId, campaignId, webId }: Props) {
  const { showError, showSuccess, showErrorUnless422 } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [existingSettings, setExistingSettings] = useState<AssetWorkflowSettingsViewDto | null>(null)
  const { getAssetStatuses } = useLookups()
  const { canEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
  const qaVisible = useAssetQaVisible()

  const form = useForm<WorkflowFormData>({
    mode: 'onChange',
    defaultValues: {
      inheritFromParent: level !== 'workspace',
      defaultReviewerUsers: [],
      internalTeamUsers: [],
      defaultStatusOnUpload: '',
      requireSignOffConfirmation: false,
      allowCommentsAfterSignOff: true,
      internalOnlyReopen: false,
      holdVerdictWhenBrandChecksCannotRun: true,
      allowImages: true,
      allowDocuments: true,
      allowVideos: true,
      allowArchives: true,
      allowedExtensions: ''
    }
  })

  const { register, handleSubmit, formState, control, reset } = form
  const { errors, isDirty } = formState

  const inheritFromParent = form.watch('inheritFromParent')
  const showForm = level === 'workspace' || !inheritFromParent

  // Fetch settings based on level
  const fetchSettings = async () => {
    switch (level) {
      case 'workspace':
        return workflowSettingsApi.getWorkspaceSettings(clientCompanyId, workspaceId)
      case 'campaign':
        return workflowSettingsApi.getCampaignSettings(clientCompanyId, workspaceId, campaignId!)
      case 'web':
        return workflowSettingsApi.getWebSettings(clientCompanyId, workspaceId, webId!)
    }
  }

  // Save settings based on level
  const saveSettings = async (dto: AssetWorkflowSettingsDto) => {
    switch (level) {
      case 'workspace':
        return workflowSettingsApi.updateWorkspaceSettings(clientCompanyId, workspaceId, dto)
      case 'campaign':
        return workflowSettingsApi.updateCampaignSettings(clientCompanyId, workspaceId, campaignId!, dto)
      case 'web':
        return workflowSettingsApi.updateWebSettings(clientCompanyId, workspaceId, webId!, dto)
    }
  }

  // Helper to populate form from settings response
  const populateForm = (settings: AssetWorkflowSettingsViewDto) => {
    reset({
      inheritFromParent: settings.InheritFromParent,
      defaultReviewerUsers: settings.DefaultReviewerUsers || [],
      internalTeamUsers: settings.InternalTeamUsers || [],
      defaultStatusOnUpload: settings.DefaultStatusOnUpload || '',
      requireSignOffConfirmation: settings.RequireSignOffConfirmation || false,
      allowCommentsAfterSignOff: settings.AllowCommentsAfterSignOff !== false,
      internalOnlyReopen: settings.InternalOnlyReopen || false,
      // ⚑ `!== false` and an optional chain, because this one defaults to ON and the whole AssetQa
      // object is absent on any settings record written before ORB-840. Both absences have to read
      // as the default — `|| false` here would show "off" for a workspace the server treats as on.
      holdVerdictWhenBrandChecksCannotRun:
        settings.AssetQa?.HoldVerdictWhenBrandChecksCannotRun !== false,
      allowImages: settings.FileRestrictions.AllowImages !== false,
      allowDocuments: settings.FileRestrictions.AllowDocuments !== false,
      allowVideos: settings.FileRestrictions.AllowVideos !== false,
      allowArchives: settings.FileRestrictions.AllowArchives !== false,
      allowedExtensions: settings.FileRestrictions.AllowedExtensions?.join(', ') || ''
    })
  }

  // Load existing settings
  useEffect(() => {
    let active = true

    const loadData = async () => {
      setLoading(true)

      const settingsResponse = await fetchSettings()
      if (!active) return

      if (settingsResponse.data) {
        const settings = settingsResponse.data
        setExistingSettings(settings)
        populateForm(settings)
      } else if (settingsResponse.error && settingsResponse.status !== 404) {
        showError('Failed to load settings', settingsResponse.error)
      }

      setLoading(false)
    }

    loadData()

    return () => {
      active = false
    }
  }, [level, clientCompanyId, workspaceId, campaignId, webId, showError, reset])

  const onSubmit = handleSubmit(async (data) => {
    if (!canEdit) return
    setSaving(true)

    const allowedExtensions = data.allowedExtensions
      ? data.allowedExtensions.split(',').map(ext => ext.trim()).filter(Boolean)
      : undefined

    const dto: AssetWorkflowSettingsDto = {
      WorkspaceId: workspaceId,
      CampaignId: campaignId,
      WebId: webId,
      InheritFromParent: level === 'workspace' ? false : data.inheritFromParent,
      DefaultReviewerUserIds: data.defaultReviewerUsers.map(u => u.Id),
      InternalTeamUserIds: data.internalTeamUsers.map(u => u.Id),
      DefaultStatusOnUpload: data.defaultStatusOnUpload,
      RequireSignOffConfirmation: data.requireSignOffConfirmation,
      AllowCommentsAfterSignOff: data.allowCommentsAfterSignOff,
      InternalOnlyReopen: data.internalOnlyReopen,
      AssetQa: {
        HoldVerdictWhenBrandChecksCannotRun: data.holdVerdictWhenBrandChecksCannotRun
      },
      FileRestrictions: {
        AllowImages: data.allowImages,
        AllowDocuments: data.allowDocuments,
        AllowVideos: data.allowVideos,
        AllowArchives: data.allowArchives,
        // Echoed back untouched, not recomputed. The input is gone from this form but the field is
        // still on the API contract, where it defaults to 500 MB when absent — omitting it would
        // quietly rewrite whatever a web already had stored. Undefined only when there is no
        // settings document yet, in which case there is nothing to preserve (ORB-721).
        MaxFileSizeBytes: existingSettings?.FileRestrictions?.MaxFileSizeBytes,
        AllowedExtensions: allowedExtensions
      },
      ETag: existingSettings?.ETag
    }

    const response = await saveSettings(dto)

    if (response.data) {
      setExistingSettings(response.data)
      showSuccess('Settings saved', 'Workflow settings have been updated successfully.')
      reset(data)
      // Refresh the effective settings signal so modals pick up changes
      if (level === 'web' && campaignId && webId) {
        loadEffectiveSettings(clientCompanyId, workspaceId, campaignId, webId)
      }
    } else if (response.error) {
      if (response.status === 422) {
        setFormValidationErrors(response, form.setError)
      } else {
        showErrorUnless422('Save failed', response, 'Unable to save workflow settings')
      }
    }

    setSaving(false)
  })

  // Handle toggling inheritance on/off
  const handleInheritToggle = async (checked: boolean) => {
    if (!canEdit) return
    form.setValue('inheritFromParent', checked, { shouldDirty: true })

    if (checked) {
      // Save immediately with InheritFromParent=true
      setSaving(true)
      const dto: AssetWorkflowSettingsDto = {
        WorkspaceId: workspaceId,
        CampaignId: campaignId,
        WebId: webId,
        InheritFromParent: true,
        DefaultReviewerUserIds: existingSettings?.DefaultReviewerUsers?.map(u => u.Id) || [],
        InternalTeamUserIds: existingSettings?.InternalTeamUsers?.map(u => u.Id) || [],
        DefaultStatusOnUpload: existingSettings?.DefaultStatusOnUpload || '',
        RequireSignOffConfirmation: existingSettings?.RequireSignOffConfirmation || false,
        AllowCommentsAfterSignOff: existingSettings?.AllowCommentsAfterSignOff !== false,
        InternalOnlyReopen: existingSettings?.InternalOnlyReopen || false,
        // Built from existingSettings, not from form state — so anything added to the save above
        // and not here is quietly dropped the moment someone turns inheritance on.
        AssetQa: {
          HoldVerdictWhenBrandChecksCannotRun:
            existingSettings?.AssetQa?.HoldVerdictWhenBrandChecksCannotRun !== false
        },
        FileRestrictions: existingSettings?.FileRestrictions || {
          AllowImages: true,
          AllowDocuments: true,
          AllowVideos: true,
          AllowArchives: true,
        },
        ETag: existingSettings?.ETag
      }

      const response = await saveSettings(dto)
      if (response.data) {
        setExistingSettings(response.data)
        showSuccess('Settings saved', 'Now inheriting settings from the parent level.')
        form.reset({ ...form.getValues(), inheritFromParent: true })
        // Refresh the effective settings signal so modals pick up changes
        if (level === 'web' && campaignId && webId) {
          loadEffectiveSettings(clientCompanyId, workspaceId, campaignId, webId)
        }
      } else if (response.error) {
        showErrorUnless422('Save failed', response, 'Unable to save settings')
        form.setValue('inheritFromParent', false)
      }
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (existingSettings) {
      populateForm(existingSettings)
    }
  }

  const isSaveDisabled = saving || !formState.isValid || !isDirty
  const saveText = saving ? 'Saving...' : isDirty ? 'Save Changes' : 'No Changes'

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300">
        <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        Loading workflow settings...
      </div>
    )
  }

  const assetStatuses = getAssetStatuses()
  const statusOptions = assetStatuses.map(s => ({ value: s.Description, label: s.Description }))

  return (
    <div>
      {/* Inheritance toggle for campaign and web levels */}
      {level !== 'workspace' && (
        <InheritSettingsToggle
          level={level}
          checked={inheritFromParent}
          onChange={handleInheritToggle}
          disabled={!canEdit}
        />
      )}

      {/* Settings form - only shown when not inheriting */}
      {showForm && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <span className="relative inline-flex">
              <SettingsIcon className="w-5 h-5 text-primary-600 dark:text-primary-500" />
            </span>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">
              Asset Workflow Settings
            </h2>
            <button
              type="submit"
              form="asset-workflow-settings-form"
              disabled={isSaveDisabled || !canEdit}
              className={`ml-auto px-4 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${BRAND_CLASSES.BUTTON.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saveText}
            </button>
          </div>

          <form id="asset-workflow-settings-form" onSubmit={onSubmit} className="space-y-8">
            {/* Team Members Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                <Users className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                  Team Members
                </h3>
              </div>

              <Controller
                name="defaultReviewerUsers"
                control={control}
                render={({ field, fieldState }) => (
                  <UserPicker
                    label="Default Reviewers"
                    placeholder="Search for reviewers..."
                    helperText="Users who will review and approve assets by default"
                    workspaceId={workspaceId}
                    clientCompanyId={clientCompanyId}
                    userType="reviewer"
                    selectedUsers={field.value ?? NO_SELECTED_USERS}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    containerClassName="grid-cols-1"
                  />
                )}
              />

              <Controller
                name="internalTeamUsers"
                control={control}
                render={({ field, fieldState }) => (
                  <UserPicker
                    label="Internal Team"
                    placeholder="Search for internal team members..."
                    helperText="Internal team members who can manage the workflow"
                    workspaceId={workspaceId}
                    clientCompanyId={clientCompanyId}
                    userType="internal"
                    selectedUsers={field.value ?? NO_SELECTED_USERS}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    containerClassName="grid-cols-1"
                  />
                )}
              />
            </div>

            {/* Approval Settings Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                <Shield className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                  Approval Settings
                </h3>
              </div>

              <Select
                label="Default Status on Upload"
                required
                placeholder="Select default status"
                helperText="Status that assets will receive when first uploaded"
                options={statusOptions}
                error={errors.defaultStatusOnUpload?.message}
                {...register('defaultStatusOnUpload', {
                  required: 'Default status is required'
                })}
              />

              <div className="space-y-3">
                <Checkbox
                  label="Require Sign-Off Confirmation"
                  description="Users must explicitly confirm when signing off on assets"
                  {...register('requireSignOffConfirmation')}
                />
                <Checkbox
                  label="Allow Comments After Sign-Off"
                  description="Enable commenting on assets even after they've been approved"
                  {...register('allowCommentsAfterSignOff')}
                />
                <Checkbox
                  label="Internal Only Reopen"
                  description="Only internal team members can reopen approved assets"
                  {...register('internalOnlyReopen')}
                />
              </div>
            </div>

            {/* Quality Assurance Section.

                ⚑⚑ Hidden from a customer along with every other QA surface (ORB-982). Hiding it does
                NOT change what is saved: the form field is seeded from the loaded settings in
                `resetForm`, so an untouched hidden checkbox submits the workspace's current value
                back unchanged. A workspace does not silently lose its hold-the-verdict policy
                because a customer admin saved an unrelated file restriction. */}
            {qaVisible && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                <ScanSearch className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                  Quality Assurance
                </h3>
              </div>

              <div className="space-y-3">
                <Checkbox
                  label="Hold the verdict when brand colours can't be checked"
                  description="The colour check needs a confirmed brand palette. Leave this on and an asset it couldn't measure is reported as needing a look; turn it off and only the checks that ran decide the verdict."
                  {...register('holdVerdictWhenBrandChecksCannotRun')}
                />
              </div>
            </div>
            )}

            {/* File Restrictions Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                <FileCheck className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                  File Restrictions
                </h3>
              </div>

              <div>
                <label className={`block text-sm font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-1`}>
                  Allowed File Types
                </label>
                <p className={`text-xs ${BRAND_CLASSES.INPUT.HELPER} mb-4`}>
                  Unchecked file types will not be accepted for upload
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { field: 'allowImages' as const, label: 'Images', extensions: 'jpg, jpeg, png, gif, svg, webp, bmp, tiff' },
                    { field: 'allowDocuments' as const, label: 'Documents', extensions: 'pdf, doc, docx, ppt, pptx, xls, xlsx, txt, rtf' },
                    { field: 'allowVideos' as const, label: 'Videos', extensions: 'mp4, mov, avi, wmv, mkv, webm' },
                    { field: 'allowArchives' as const, label: 'Archives', extensions: 'zip, rar, tar, gz, 7z' },
                  ]).map(({ field, label, extensions }) => {
                    const checked = form.watch(field)
                    return (
                      <Checkbox
                        key={field}
                        containerClassName={`relative p-4 rounded-xl border cursor-pointer transition-all ${
                          checked
                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/15 shadow-sm'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                        label={label}
                        description={extensions}
                        {...register(field)}
                      />
                    )
                  })}
                </div>
              </div>

              <div>
                <label
                  htmlFor="allowedExtensions"
                  className={`block text-sm font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-2`}
                >
                  Allowed Extensions
                  <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                    (Optional)
                  </span>
                </label>
                <input
                  type="text"
                  id="allowedExtensions"
                  placeholder=".pdf, .docx, .jpg, .png"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`}
                  {...register('allowedExtensions')}
                />
                <p className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>
                  Comma-separated list of specific file extensions to allow (leave empty for all)
                </p>
              </div>
            </div>

            {/* Form Actions */}
            <FormActions
              isSubmitting={saving}
              isSaveDisabled={isSaveDisabled || !canEdit}
              onCancel={handleCancel}
              saveText={saveText}
            />
          </form>
        </div>
      )}
    </div>
  )
}
